import React from 'react';
import Icon from './Icon';
import '../styles/components/MetricCards.css';

export type MetricTone = 'pink' | 'blue' | 'green' | 'teal' | 'orange' | 'purple' | 'red';

interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  tone?: MetricTone;
  icon?: string;
  onClick?: () => void;
  /** Highlights the card as the active filter (only meaningful with onClick) */
  active?: boolean;
  /** Native tooltip — lets an abbreviated value (₦23.431M) still expose the
   *  exact figure on hover instead of losing it. */
  title?: string;
  /** Extra content pinned to the right of the card (e.g. a secondary
   *  total like CBM/cost alongside the primary count) — kept separate
   *  from `value`/`label` so callers with just a count don't have to
   *  restructure anything to add one. */
  extra?: React.ReactNode;
  /** One entry per currency. With two or more, they are stacked one per line
   *  in place of `value` so none is cut off; `value` still serves as the
   *  fallback for a single amount. */
  lines?: string[];
}

interface ProgressMetricCardProps {
  label: string;
  value: React.ReactNode;
  progress: number;
  tone?: MetricTone;
  icon?: string;
  /** Extra content below the label — e.g. an over-cap warning — so a card
   *  that needs more than value/bar/label stays one card instead of a
   *  second element the caller has to lay out (and grid-span) itself. */
  footer?: React.ReactNode;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  tone = 'blue',
  icon,
  onClick,
  active,
  title,
  extra,
  lines,
}) => {
  const content = (
    <>
      {icon && (
        <div className="metric-icon">
          <Icon name={icon} size={18} />
        </div>
      )}
      <div className="metric-content">
        {lines && lines.length > 1 ? (
          <div className="metric-value metric-value--lines">
            {lines.map(line => <div key={line} className="metric-value-line">{line}</div>)}
          </div>
        ) : (
          <div className="metric-value">{value}</div>
        )}
        <div className="metric-label">{label}</div>
      </div>
      {extra && <div className="metric-extra">{extra}</div>}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`metric-card metric-card--${tone}${active ? ' active' : ''}`}
        onClick={onClick}
        title={title}
        aria-pressed={active ?? false}
      >
        {content}
      </button>
    );
  }

  return <div className={`metric-card metric-card--${tone}`} title={title}>{content}</div>;
};

export const ProgressMetricCard: React.FC<ProgressMetricCardProps> = ({
  label,
  value,
  progress,
  tone = 'green',
  icon,
  footer,
}) => {
  const safeProgress = Math.max(0, Math.min(100, progress));

  return (
    <div className={`metric-card metric-card--progress metric-card--${tone}`}>
      {icon && (
        <div className="metric-icon">
          <Icon name={icon} size={18} />
        </div>
      )}
      <div className="metric-content" style={ { flex: 1, minWidth: 0 }}>
        <div className="metric-progress-row">
          <div className="metric-value">{value}</div>
          <div className="metric-progress">
            <div
              className="metric-progress-fill"
              style={ { width: `${safeProgress}%` }}
            />
          </div>
        </div>
        <div className="metric-label">{label}</div>
        {footer}
      </div>
    </div>
  );
};